import React, { useState, useRef, KeyboardEventHandler, useEffect, ReactNode } from 'react';
import Autosuggest, { GetSuggestionValue, SuggestionSelectedEventData } from 'react-autosuggest';
import { useForm, useFormState } from 'react-final-form';

// Types
import type { WMarketplaceLocationEntity } from '@zola-helpers/client/dist/es/api/marketplace';

// Util
import { setCookie, deleteCookie } from '@zola-helpers/client/dist/es/util/storage';
import { debouncedFetchSuggestedLocations, parseCityState } from 'helpers/weddingLocation';

// Components
import { FormLabel } from '@zola/zola-ui/src/components/Form/form.styles';
import WeddingDetails1 from 'components/onboard/onboard-v2/DefaultOnboardPanes/WeddingDetails1';

// Styles
import {
  AutoSuggestContainer,
  SuggestionContainer,
  StyledRemoveIcon,
  StyledCheckboxField,
  StyledOnboardStep,
} from './WeddingLocationStep.styles';

type WeddingLocationStepProps = {
  hed?: string | ReactNode;
  dek?: string | ReactNode | null;
  className?: string;
};

const WeddingLocationStep = ({
  hed = "Now, let's talk about ✨The Day✨",
  dek = "We'll help you plan the wedding you want—small or big, near or far.",
  className,
}: WeddingLocationStepProps): JSX.Element => {
  const { values } = useFormState();
  const { change, blur } = useForm();
  const [cityStateValue, setCityStateValue] = useState(values?.cityStateValue);
  const [inputValue, setInputValue] = useState(values?.cityStateValue);
  const [skipValue, setSkipValue] = useState(!!values?.skip_location);
  const inputElement = useRef<HTMLInputElement | null>(null);
  const [suggestedLocations, setSuggestedLocations] = useState<WMarketplaceLocationEntity[]>([]);

  // --------------------------------------------------------------------------
  // Begin Auto-Suggest
  // --------------------------------------------------------------------------
  const onSuggestionSelected = (
    event: React.FormEvent,
    { suggestion: location }: SuggestionSelectedEventData<WMarketplaceLocationEntity>
  ): void => {
    event.preventDefault();
    const { type, name, slug } = location;

    if (type === 'US_CITY' || type === 'NOT_LISTED') {
      setCityStateValue(name);
      deleteCookie('citySlug');
      if (slug) setCookie('citySlug', slug, 1);
      if (type === 'US_CITY') {
        const { city = '', state_province = '' } = parseCityState(name) || {};
        change('city', city);
        change('state_province', state_province);
      }
      change('cityStateValue', name);
    } else if (inputElement.current) {
      inputElement.current.focus();
    }

    const suggestEl = document?.getElementById('location-autosuggest');
    if (location.type !== 'PLACEHOLDER' && suggestEl) {
      suggestEl.blur();
    }
  };

  // Used as the value for the input
  const getSuggestionValue: GetSuggestionValue<WMarketplaceLocationEntity> = suggestion => {
    if (suggestion?.type === 'US_CITY' || suggestion?.type === 'NOT_LISTED') {
      return suggestion.name;
    }
    return '';
  };

  // Used in render suggestion
  const getSuggestionText = (location: WMarketplaceLocationEntity): string | null => {
    if (location.type === 'PLACEHOLDER') {
      return location.name;
    }
    return getSuggestionValue(location);
  };
  const handleEnterKeyPress = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const suggestion = suggestedLocations && suggestedLocations[0];
      if (suggestion) {
        onSuggestionSelected((event as unknown) as React.FormEvent, {
          suggestion,
          suggestionValue: suggestion.name,
          suggestionIndex: 0,
          sectionIndex: 0,
          method: 'enter',
        });
      }
      const suggestEl = document?.getElementById('location-autosuggest');
      if (suggestEl) suggestEl.blur();
    }
  };

  const renderSuggestion = (location: WMarketplaceLocationEntity): JSX.Element => (
    <SuggestionContainer>{getSuggestionText(location)}</SuggestionContainer>
  );

  const resetAutosuggestField = (): void => {
    setInputValue('');
    setCityStateValue('');
    setSuggestedLocations([]);
    deleteCookie('citySlug');
    change('city', '');
    change('state_province', '');
    change('cityStateValue', '');
  };

  // --------------------------------------------------------------------------
  // END Auto-Suggest
  // --------------------------------------------------------------------------

  useEffect(() => {
    // Validation is not working for fields not in the dom when the form is initialized.
    // blur() of one field triggers validation for the rest of the fields on page
    // TODO: Find a sustainable solution/fix for validating new form fields in react-final-form
    blur('skip_location');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledOnboardStep
      hed={hed}
      dek={dek}
      portalContent={<WeddingDetails1 />}
      isValid={values?.cityStateValue || skipValue}
      className={className}
    >
      <AutoSuggestContainer>
        <FormLabel htmlFor="location-autosuggest">
          Where are you getting married? (Best guesses welcome!)
        </FormLabel>
        <Autosuggest<WMarketplaceLocationEntity>
          suggestions={suggestedLocations}
          onSuggestionSelected={onSuggestionSelected}
          onSuggestionsFetchRequested={({ value }): void =>
            debouncedFetchSuggestedLocations(value, setSuggestedLocations)
          }
          onSuggestionsClearRequested={(): void => setSuggestedLocations([])}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={{
            id: 'location-autosuggest',
            value: inputValue || '',
            autoComplete: 'off',
            autoFocus: false,
            placeholder: 'City or nearby area',
            ref: inputElement,
            onFocus: (): void => {
              setSkipValue(false);
              resetAutosuggestField();
            },
            onKeyUp: (handleEnterKeyPress as unknown) as KeyboardEventHandler<HTMLElement>,
            onChange: (event, { newValue }): void => {
              setInputValue(newValue);
            },
          }}
        />
        {cityStateValue && (
          <StyledRemoveIcon onClick={resetAutosuggestField} height={17} width={17} />
        )}
      </AutoSuggestContainer>
      <StyledCheckboxField
        id="skip_location"
        name="skip_location"
        label="We're still deciding"
        checked={skipValue}
        onChange={(): void => {
          setSkipValue(!skipValue);
          resetAutosuggestField();
        }}
      />
    </StyledOnboardStep>
  );
};

export default WeddingLocationStep;
